import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Card,
  Button,
  message,
  Modal,
  Form,
  Input,
  Upload,
  Row,
  Col,
} from "antd";
import {
  UploadOutlined,
  InboxOutlined,
  SearchOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import createInvoiceColumns from "./Invoices/invoiceColumns";
import CreateInvoiceModal from "./Shared/Forms/CreateInvoiceModal";
import {
  getAllInvoices,
  deleteInvoice,
  getInvoiceFilterValues,
  getAllPartners,
  uploadBillsFile,
  getLatestBillsFile,
  exportInvoices,
  exportInvoicePDF,
} from "../api/accounting";
import { uploadEmail } from "../../../core/api/settings";
import { useNavigate } from "react-router-dom";
import moment from "moment";
// import { getProfileTest } from "../../../core/api/auth";
import { useSelector } from "react-redux";
import { useRef } from "react";
import ImageWithBasePath from "../../../core/img/imagewithbasebath";

const Invoices = ({ filters = {}, selectedBrand = null }) => {
  const profile = useSelector((state) => state.profile);

  console.log("Profile in Invoices component:", profile);
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploadType, setUploadType] = useState("bill"); // 'bill' or 'invoice'
  const [uploadLoading, setUploadLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  // const [partners, setPartners] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPartner, setSelectedPartner] = useState(null);
  // const [loadingPartners, setLoadingPartners] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [createInvoiceModalVisible, setCreateInvoiceModalVisible] =
    useState(false);
  const [pageSize, setPageSize] = useState(100);
  const [totalItems, setTotalItems] = useState(0);
  // Add state for sorting and filtering
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [tableFilters, setTableFilters] = useState({});
  // State for latest bills file
  const [latestBillsFile, setLatestBillsFile] = useState(null);
  const [loadingLatestFile, setLoadingLatestFile] = useState(false);
  const [userPermissions, setUserPermissions] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  // Excel export related states
  const [isExportModalVisible, setIsExportModalVisible] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");

  // Local state for search input value (not yet applied to API call)
  const [searchInputValue, setSearchInputValue] = useState("");

  // Refs to store previous filters and sort state for comparison
  const prevFiltersRef = useRef({});
  const prevSortRef = useRef({});

  // State for filter values
  const [filterValues, setFilterValues] = useState({
    from: [],
    to: [],
    invoice_type: [],
    payment_status: [],
    status: [],
  });
  const [summary, setSummary] = useState({
    count: 0,
    total_amount: 0,
    paid_amount: 0,
  });
  const [form] = Form.useForm();
  // const navigate = useNavigate();

  // Load user profile on component mount
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (profile) {
        setUserPermissions(profile?.page_authorization || []);
        setUserRole(profile?.role || null);
        console.log("User role:", profile?.role);
        console.log("User permissions:", profile?.page_authorization);
      }
    };
    fetchUserProfile();
  }, [profile]);

  const hasPermission = useCallback(
    (pathPrefix) => {
      const cleanedPrefix = pathPrefix.replace(/\*$/, "");
      if (userRole === "super_admin") {
        return true;
      }

      return userPermissions.some((perm) => perm.startsWith(cleanedPrefix));
    },
    [userRole, userPermissions]
  );

  // Fetch the latest bills file for the current brand
  const fetchLatestBillsFile = useCallback(async () => {
    if (!selectedBrand) return;

    setLoadingLatestFile(true);
    try {
      const response = await getLatestBillsFile(selectedBrand.id);
      setLatestBillsFile(response);
    } catch (error) {
      console.error("Error fetching latest bills file:", error);
      // Don't show error message to user as this is not critical
      setLatestBillsFile(null);
    } finally {
      setLoadingLatestFile(false);
    }
  }, [selectedBrand]);

  // Fetch partners for dropdown
  // const fetchPartners = useCallback(async () => {
  //   setLoadingPartners(true);
  //   try {
  //     const response = await getAllPartners();
  //     if (Array.isArray(response)) {
  //       setPartners(response);
  //     } else {
  //       console.error("Invalid partners response format:", response);
  //       setPartners([]);
  //     }
  //   } catch (error) {
  //     console.error("Error fetching partners:", error);
  //     message.error("Failed to load partners");
  //     setPartners([]);
  //   } finally {
  //     setLoadingPartners(false);
  //   }
  // }, []);

  // Fetch filter values from the backend
  const fetchFilterValues = useCallback(async () => {
    try {
      const brandId = selectedBrand ? selectedBrand.id : null;
      const data = await getInvoiceFilterValues("all", brandId);

      // Ensure unique values for all filter types
      const processedData = {
        ...data,
        // Ensure unique invoice_type values
        invoice_type: data.invoice_type ? [...new Set(data.invoice_type)] : [],
        // Ensure unique status values
        status: data.status ? [...new Set(data.status)] : [],
        // Payment status is already fixed in the backend, but ensure it's consistent
        payment_status: data.payment_status || [
          "fully_paid",
          "partially_paid",
          "unpaid",
        ],
      };

      setFilterValues(processedData);
      console.log("Filter values loaded:", processedData);
    } catch (error) {
      console.error("Error fetching filter values:", error);
      message.error("Failed to load filter options");
    }
  }, [selectedBrand]);

  // Format invoice data for display
  const formatInvoiceData = useCallback((invoiceData) => {
    return invoiceData.map((invoice) => {
      // Calculate payment percentage
      const total = parseFloat(invoice.total_amount || 0);
      const allocatedAmount = parseFloat(
        invoice.allocated_amount || invoice.paid_amount || 0
      );
      const percentPaid = total > 0 ? (allocatedAmount / total) * 100 : 0;

      // Determine allocation status
      let allocationStatus;
      if (percentPaid >= 99.99) {
        allocationStatus = {
          type: "fully_paid",
          label: "Fully Paid",
          color: "success",
        };
      } else if (percentPaid > 0) {
        allocationStatus = {
          type: "partially_paid",
          label: "Partially Paid",
          color: "warning",
        };
      } else {
        allocationStatus = {
          type: "unpaid",
          label: "Unpaid",
          color: "default",
        };
      }

      // Count items if available
      const itemsCount = Array.isArray(invoice.items)
        ? invoice.items.length
        : 0;

      return {
        id: invoice.id,
        number: invoice.number || `INV-${invoice.id}`,
        partner: invoice.partner?.name || "Unknown",
        partnerType: invoice.invoice_type === "invoice" ? "customer" : "vendor",
        invoice_type: invoice.invoice_type || "invoice",
        issueDate: invoice.issue_date,
        dueDate: invoice.due_date,
        // Map backend fields to frontend display fields with proper defaults
        subtotal: invoice.subtotal || 0,
        discount_amount: invoice.discount_amount || 0,
        tax_rate: invoice.tax_rate || 0,
        total: parseFloat(invoice.total_amount || 0).toFixed(2),
        paid: parseFloat(invoice.paid_amount || 0).toFixed(2),
        paid_amount: parseFloat(invoice.paid_amount || 0),
        status: invoice.status || "draft",
        allocations: invoice.payment_allocations || invoice.allocations || [],
        items_count: itemsCount,
        currency: invoice.currency || "$",
        percentPaid,
        allocationStatus,
        last_modified_by: invoice.last_modified_by || "Unknown",
        originalData: invoice, // Keep the original data for reference
      };
    });
  }, []);

  const processInvoiceResponse = useCallback(
    (response) => {
      let invoiceData = [];
      if (response && response.results) {
        if (response.timing) {
          console.log("Backend timing:", response.timing);
          console.log(
            `API performance: Total time: ${response.timing.total_time}s, Query time: ${response.timing.query_time}s`
          );
        }
        invoiceData = response.results;
        setTotalItems(response.total || 0);
        if (response.summary) {
          setSummary(response.summary);
          console.log("Summary statistics:", response.summary);
        }
      } else if (Array.isArray(response)) {
        invoiceData = response;
      }

      if (!invoiceData || !Array.isArray(invoiceData)) {
        console.error("Invalid response format:", response);
        message.error("Received invalid data format from server");
        setInvoices([]);
        return;
      }

      console.log("Raw invoice data from backend:", invoiceData);

      const formattedInvoices = formatInvoiceData(invoiceData);

      setInvoices(formattedInvoices);
    },
    [formatInvoiceData]
  );

  const fetchInvoicesWithParams = useCallback(
    (
      tableFiltersParam,
      sortFieldParam,
      sortOrderParam,
      pageParam,
      pageSizeParam
    ) => {
      setLoading(true);
      try {
        // Create API filters object directly
        const apiFilters = {
          include_allocations: true,
          page: pageParam || 1,
          page_size: pageSizeParam || pageSize,
        };

        // Add component filters
        if (filters.dateRange && filters.dateRange.length === 2) {
          if (filters.dateRange[0])
            apiFilters.start_date = filters.dateRange[0].format("YYYY-MM-DD");
          if (filters.dateRange[1])
            apiFilters.end_date = filters.dateRange[1].format("YYYY-MM-DD");
        }

        if (filters.partnerType && filters.partnerType !== "all") {
          apiFilters.partner_type = filters.partnerType;
        }

        if (filters.status && filters.status !== "all") {
          apiFilters.status = filters.status;
        }

        // Add brand and partner filters
        if (selectedBrand) apiFilters.brand = selectedBrand.id;
        if (selectedPartner) apiFilters.partner_id = selectedPartner;
        if (searchQuery && searchQuery.trim() !== "")
          apiFilters.search = searchQuery.trim();

        // Add sorting
        if (sortFieldParam) {
          apiFilters.sort_field = sortFieldParam;
          apiFilters.sort_order = sortOrderParam;
        } else if (sortField) {
          apiFilters.sort_field = sortField;
          apiFilters.sort_order = sortOrder;
        }

        // Add table filters - handle multiple selections by joining with commas
        if (tableFiltersParam) {
          if (
            tableFiltersParam.invoice_type &&
            tableFiltersParam.invoice_type.length > 0
          ) {
            apiFilters.invoice_type = tableFiltersParam.invoice_type.join(",");
          }

          if (
            tableFiltersParam.payment_status &&
            tableFiltersParam.payment_status.length > 0
          ) {
            apiFilters.payment_status =
              tableFiltersParam.payment_status.join(",");
            console.log(
              "Applying payment status filter:",
              tableFiltersParam.payment_status
            );
          }

          if (tableFiltersParam.status && tableFiltersParam.status.length > 0) {
            apiFilters.status = tableFiltersParam.status.join(",");
          }

          if (tableFiltersParam.from && tableFiltersParam.from.length > 0) {
            apiFilters.from = tableFiltersParam.from.join(",");
          }

          if (tableFiltersParam.to && tableFiltersParam.to.length > 0) {
            apiFilters.to = tableFiltersParam.to.join(",");
          }
        }

        console.log("Fetching invoices with immediate params:", apiFilters);

        getAllInvoices(apiFilters)
          .then((response) => {
            processInvoiceResponse(response);
            setLoading(false);
          })
          .catch((error) => {
            console.error("Error fetching invoices:", error);
            message.error("Failed to load invoices");
            setInvoices([]);
            setTotalItems(0);
            setLoading(false);
          });
      } catch (error) {
        console.error("Error preparing filters:", error);
        message.error("Failed to prepare filters");
        setLoading(false);
      }
    },
    [
      filters,
      selectedBrand,
      selectedPartner,
      searchQuery,
      pageSize,
      sortField,
      sortOrder,
      processInvoiceResponse,
    ]
  );

  // Main fetch invoices function that uses component state
  const fetchInvoices = useCallback(() => {
    console.log("Fetching invoices with current state");
    // Use the fetchInvoicesWithParams function with current state values
    fetchInvoicesWithParams(
      tableFilters,
      sortField,
      sortOrder,
      currentPage,
      pageSize
    );
  }, [
    fetchInvoicesWithParams,
    tableFilters,
    sortField,
    sortOrder,
    currentPage,
    pageSize,
  ]);

  // Handle search input change (just updates local state, doesn't trigger API call)
  const handleSearchChange = (e) => {
    setSearchInputValue(e.target.value);
  };

  // Handle search button click
  const handleSearchClick = () => {
    setSearchQuery(searchInputValue);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle Enter key press in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearchClick();
    }
  };

  // Handle partner selection
  // const handlePartnerChange = (value) => {
  //   setSelectedPartner(value);
  //   setCurrentPage(1); // Reset to first page when changing filters
  // };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle table changes including sorting and filtering
  const handleTableChange = (pagination, filters, sorter) => {
    const current = pagination.current;
    const size = pagination.pageSize;

    const sortFieldMapping = {
      from: "from",
      to: "to",
      partner: "partner__name",
      issueDate: "issue_date",
      dueDate: "due_date",
      invoice_type: "invoice_type",
      status: "status",
      items_count: "items_count",
    };

    const mappedField = sorter?.field
      ? sortFieldMapping[sorter.field] || sorter.field
      : null;
    const order = sorter?.order || null;

    // Compare filters
    const prevFilters = prevFiltersRef.current;
    const prevSort = prevSortRef.current;

    const isFilterChanged =
      JSON.stringify(filters) !== JSON.stringify(prevFilters);
    const isSortChanged =
      prevSort.field !== mappedField || prevSort.order !== order;
    const isPageSizeChanged = size !== pageSize;

    // Determine the page to use
    const newPage =
      isFilterChanged || isSortChanged || isPageSizeChanged ? 1 : current;

    // Update refs
    prevFiltersRef.current = filters;
    prevSortRef.current = { field: mappedField, order };

    // Update state
    setSortField(mappedField);
    setSortOrder(order);
    setTableFilters(filters);
    setPageSize(size);
    setCurrentPage(newPage);

    fetchInvoicesWithParams(filters, mappedField, order, newPage, size);
  };

  // Effect for fetching partners - only runs once on mount
  // useEffect(() => {
  //   fetchPartners();
  // }, [fetchPartners]);

  // Effect for fetching latest bills file - runs when selectedBrand changes
  useEffect(() => {
    fetchLatestBillsFile();
  }, [fetchLatestBillsFile]);

  // Effect for fetching filter values - runs when selectedBrand changes
  useEffect(() => {
    fetchFilterValues();
  }, [fetchFilterValues]);

  // Effect for resetting page when brand changes
  useEffect(() => {
    setCurrentPage(1); // Reset to first page when brand changes
  }, [selectedBrand]);

  // Effect for fetching invoices - this is the main data fetching effect
  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleDeleteInvoice = (id) => {
    Modal.confirm({
      title: "Are you sure you want to delete this invoice?",
      content: "This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteInvoice(id);
          message.success("Invoice deleted successfully");
          fetchInvoices(); // Refresh the list
        } catch (error) {
          console.error("Error deleting invoice:", error);
          const backendMsg =
            error?.response?.data?.detail ||
            error?.response?.data?.message ||
            (typeof error?.response?.data === "string"
              ? error.response.data
              : null);

          if (backendMsg) {
            // Check if the error mentions allocations
            if (
              backendMsg.toLowerCase().includes("allocation") ||
              backendMsg.toLowerCase().includes("payment") ||
              backendMsg.toLowerCase().includes("credit") ||
              backendMsg.toLowerCase().includes("cash")
            ) {
              Modal.warning({
                title: "Cannot delete invoice/bill",
                content: (
                  <div>
                    <p>{backendMsg}</p>
                    <p style={{ marginTop: 16 }}>
                      <a
                        href="/accounting/payment-allocation"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#1890ff" }}
                      >
                        → Go to Payment Allocations
                      </a>
                    </p>
                    <p>
                      <a
                        href="/accounting/credit-allocation"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#1890ff" }}
                      >
                        → Go to Credit Allocations
                      </a>
                    </p>
                    <p>
                      <a
                        href="/accounting/cash-allocation"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#1890ff" }}
                      >
                        → Go to Cash Allocations
                      </a>
                    </p>
                  </div>
                ),
              });
            } else {
              Modal.warning({
                title: "Cannot delete invoice/bill",
                content: backendMsg,
              });
            }
          } else {
            Modal.warning({
              title: "Cannot delete invoice/bill",
              content: (
                <div>
                  <p>
                    This document has allocations
                    (payments/credits/cash/overpayments). Please unallocate
                    first.
                  </p>
                  <p style={{ marginTop: 16 }}>
                    <a
                      href="/accounting/payment-allocation"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#1890ff" }}
                    >
                      → Go to Payment Allocations
                    </a>
                  </p>
                  <p>
                    <a
                      href="/accounting/credit-allocation"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#1890ff" }}
                    >
                      → Go to Credit Allocations
                    </a>
                  </p>
                  <p>
                    <a
                      href="/accounting/cash-allocation"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#1890ff" }}
                    >
                      → Go to Cash Allocations
                    </a>
                  </p>
                </div>
              ),
            });
          }
        }
      },
    });
  };

  const handleEditInvoice = (invoiceId) => {
    // For now, we'll handle editing through the modal as well
    // You can extend this to pass the invoiceId to the modal
    console.log("Edit invoice:", invoiceId);
    setCreateInvoiceModalVisible(true);
  };

  const handleCreateInvoiceSuccess = () => {
    // Refresh the invoices list after successful creation
    fetchInvoices();
  };

  const handleExportPDF = async (invoiceId) => {
    try {
      const response = await exportInvoicePDF(invoiceId);

      // Create blob and download
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Get filename from response headers or use default
      const contentDisposition = response.headers["content-disposition"];
      let filename = "invoice.pdf";
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success("PDF exported successfully");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      message.error("Failed to export PDF");
    }
  };

  // Get columns from the imported function - memoize this to prevent re-creation
  const columns = React.useMemo(
    () =>
      createInvoiceColumns({
        filterValues,
        handleEditInvoice,
        handleDeleteInvoice,
        handleExportPDF,
        hasPermission,
      }),
    [
      filterValues,
      hasPermission,
      handleEditInvoice,
      handleDeleteInvoice,
      handleExportPDF,
    ]
  );

  const showUploadModal = (type) => {
    setUploadType(type);
    setUploadModalVisible(true);
  };

  const handleUploadCancel = () => {
    setUploadModalVisible(false);
    setFileList([]);
    form.resetFields();
  };

  const handleUploadOk = async () => {
    try {
      const values = await form.validateFields();

      if (fileList.length === 0) {
        message.error("Please select a file to upload");
        return;
      }

      const fileObj = fileList[0];
      const fileExtension = fileObj.name.split(".").pop().toLowerCase();

      if (fileExtension !== "xlsx" && fileExtension !== "xls") {
        message.error("Only Excel files (.xlsx, .xls) are allowed");
        return;
      }

      setUploadLoading(true);

      const billsFileData = {
        brand: selectedBrand ? selectedBrand.id : null,
        notes: values.notes || "",
        document_type: uploadType, // Send the document type to the backend
      };

      const response = await uploadBillsFile(billsFileData, fileObj);

      // Display processing results
      if (response.processing_results) {
        const {
          partners_created,
          invoices_created,
          message: resultMessage,
          error,
        } = response.processing_results;

        if (error) {
          // File was uploaded but there was an error processing it
          message.warning(
            <div>
              <p>File was uploaded but there was an error processing it:</p>
              <p>{error}</p>
            </div>,
            8 // Display for 8 seconds
          );
        } else {
          // Success case
          message.success(
            <div>
              <p>
                {uploadType === "bill" ? "Bills" : "Invoices"} file uploaded
                successfully!
              </p>
              <p>{resultMessage}</p>
              <ul>
                <li>Partners created/found: {partners_created}</li>
                <li>
                  {uploadType === "bill" ? "Bills" : "Invoices"} created:{" "}
                  {invoices_created}
                </li>
              </ul>
            </div>,
            6 // Display for 6 seconds
          );
        }
      } else {
        message.success(
          `${
            uploadType === "bill" ? "Bills" : "Invoices"
          } file uploaded successfully`
        );
      }

      // Refresh the invoices list
      fetchInvoices();

      // Refresh the latest bills file information
      fetchLatestBillsFile();

      setUploadModalVisible(false);
      setFileList([]);
      form.resetFields();
    } catch (error) {
      if (error.response && error.response.data) {
        // Handle validation errors from the backend
        const errorData = error.response.data;

        // Check for duplicate file hash error
        const errorMessage = JSON.stringify(errorData);
        if (
          errorMessage.includes("Duplicate entry") &&
          errorMessage.includes("file_hash")
        ) {
          message.error(
            "This file has already been uploaded. Please upload a different file."
          );
        } else if (typeof errorData === "object") {
          Object.keys(errorData).forEach((key) => {
            message.error(`${key}: ${errorData[key]}`);
          });
        } else {
          message.error("Failed to upload bills file: " + errorData);
        }
      } else if (
        error.message &&
        error.message.includes("Duplicate entry") &&
        error.message.includes("file_hash")
      ) {
        message.error(
          "This file has already been uploaded. Please upload a different file."
        );
      } else {
        message.error("Failed to upload bills file: " + error.message);
      }
    } finally {
      setUploadLoading(false);
    }
  };

  const uploadProps = {
    onRemove: () => {
      setFileList([]);
    },
    beforeUpload: (file) => {
      const isExcel =
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel";
      if (!isExcel) {
        message.error("You can only upload Excel files!");
      } else {
        setFileList([file]);
      }
      return false; // Prevent automatic upload
    },
    fileList,
  };

  const handleExportClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // dispatch(setToogleHeader(true)); // Collapse sidebar
    setIsExportModalVisible(true);
  };

  const exportToExcel = async (returnFile = false) => {
    try {
      // Prepare filters object to send to backend
      const exportFilters = {
        // Component filters
        brand: selectedBrand ? selectedBrand.id : null,
        partner_id: selectedPartner,
        search:
          searchQuery && searchQuery.trim() !== "" ? searchQuery.trim() : null,

        // Date range filters
        start_date:
          filters.dateRange &&
          filters.dateRange.length === 2 &&
          filters.dateRange[0]
            ? filters.dateRange[0].format("YYYY-MM-DD")
            : null,
        end_date:
          filters.dateRange &&
          filters.dateRange.length === 2 &&
          filters.dateRange[1]
            ? filters.dateRange[1].format("YYYY-MM-DD")
            : null,

        // Component status filters
        partner_type:
          filters.partnerType && filters.partnerType !== "all"
            ? filters.partnerType
            : null,
        status:
          filters.status && filters.status !== "all" ? filters.status : null,

        // Table filters - convert to arrays for backend processing
        from:
          tableFilters.from && tableFilters.from.length > 0
            ? tableFilters.from
            : null,
        to:
          tableFilters.to && tableFilters.to.length > 0
            ? tableFilters.to
            : null,
        invoice_type:
          tableFilters.invoice_type && tableFilters.invoice_type.length > 0
            ? tableFilters.invoice_type
            : null,
        payment_status:
          tableFilters.payment_status && tableFilters.payment_status.length > 0
            ? tableFilters.payment_status
            : null,

        // Sorting
        sort_field: sortField,
        sort_order: sortOrder,

        // Export format
        format: "xlsx",
      };

      if (returnFile) {
        // For email functionality, we need to get the file as blob and convert to File object
        const response = await exportInvoices(exportFilters);

        // Create a File object from the blob response
        const blob = response.data;
        const timestamp = new Date()
          .toISOString()
          .slice(0, 19)
          .replace(/:/g, "-");
        const filename = `invoices_export_${timestamp}.xlsx`;

        return new File([blob], filename, {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
      } else {
        // For direct download
        const response = await exportInvoices(exportFilters);

        // Create blob URL and trigger download
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        // Try to get filename from response headers
        const contentDisposition = response.headers["content-disposition"];
        let filename = "invoices_export.xlsx";

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(
            /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
          );
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, "");
          }
        }

        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        message.success("Export completed successfully!");
      }
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      message.error(
        "Failed to export to Excel: " +
          (error.response?.data?.error || error.message)
      );
      throw error;
    }
  };

  const handleEmailSend = async () => {
    if (!emailAddress || !emailAddress.includes("@")) {
      message.error("Please enter a valid email address");
      return;
    }

    setEmailLoading(true);
    try {
      let reportFile;
      try {
        reportFile = await exportToExcel(true);
      } catch (genError) {
        console.error("Error in file generation:", genError);
        throw genError;
      }

      const formData = new FormData();
      formData.append("attachment", reportFile, "Invoices_Export.xlsx");
      formData.append("to", emailAddress);
      formData.append("subject", "Exported Invoices Report");
      formData.append(
        "body",
        `Hello,\n\nPlease find the exported invoices Excel file attached.\n\nBest regards.`
      );

      const response = await uploadEmail(formData);

      const fileUrl = response.data.report_email.signed_attachment_url;
      const subject = encodeURIComponent("Exported Invoices Report");
      const body = encodeURIComponent(
        `Hello,\n\nPlease find the exported invoices report at the following link:\n\n${fileUrl}\n\nBest regards.`
      );

      window.location.href = `mailto:${emailAddress}?subject=${subject}&body=${body}`;
    } catch (error) {
      console.error("Error sending email:", error);
      message.error(`Failed to send email: ${error.message}`);
    } finally {
      setEmailLoading(false);
    }
  };

  const handleDownload = () => {
    exportToExcel();
    setIsExportModalVisible(false);
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          {/* Search input with button */}
          <Col xs={24} sm={12} md={8} lg={6}>
            <Input.Group compact style={{ display: "flex" }}>
              <Input
                placeholder="Search invoices..."
                prefix={<SearchOutlined />}
                onChange={handleSearchChange}
                onKeyPress={handleSearchKeyPress}
                value={searchInputValue}
                style={{ flex: 1 }}
              />
              <Button type="primary" onClick={handleSearchClick}>
                Search
              </Button>
            </Input.Group>
          </Col>
          <Col flex="auto" style={{ textAlign: "right" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              {latestBillsFile && (
                <div
                  style={{ marginRight: 16, fontSize: "13px", color: "#888" }}
                >
                  Last uploaded on:{" "}
                  {moment(latestBillsFile.date_uploaded).format(
                    "MMM DD, YYYY HH:mm"
                  )}
                </div>
              )}
              {/* Excel Export Button */}
              <Button
                onClick={handleExportClick}
                icon={
                  <ImageWithBasePath
                    src="assets/img/icons/xls.svg"
                    alt="Excel"
                    style={{ width: 16, height: 16 }}
                  />
                }
                style={{ marginRight: 8 }}
                title="Export to Excel"
              />

              <Button
                type="primary"
                onClick={() => showUploadModal("bill")}
                icon={<UploadOutlined />}
                style={{ marginRight: 8 }}
                loading={loadingLatestFile}
              >
                Upload Bills
              </Button>

              <Button
                type="primary"
                onClick={() => showUploadModal("invoice")}
                icon={<UploadOutlined />}
                style={{ marginRight: 8 }}
                loading={loadingLatestFile}
              >
                Upload Invoices
              </Button>

              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCreateInvoiceModalVisible(true)}
              >
                Create New Invoice
              </Button>
            </div>
          </Col>
        </Row>
      </div>
      <Card title="Invoices & Bills" bordered={false}>
        <Table
          columns={columns}
          dataSource={invoices}
          rowKey="id"
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalItems,
            onChange: handlePageChange,
            showSizeChanger: true,
            pageSizeOptions: [
              "100",
              "250",
              "500",
              "1000",
              "5000",
              "10000",
              "25000",
              "50000",
            ],
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} items`,
          }}
          onChange={handleTableChange}
          size="small"
          scroll={{ x: "max-content" }}
        />

        {/* Summary section showing totals and row count from backend */}
        {!loading && (
          <div
            style={{
              marginTop: 16,
              padding: "12px 16px",
              background: "#f9f9f9",
              borderRadius: "4px",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <div>
              <strong>Number of Invoices:</strong>{" "}
              {summary.count.toLocaleString()}
            </div>
            <div>
              <strong>Total Amount:</strong> USD{" "}
              {summary.total_amount.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
            <div>
              <strong>Total Paid:</strong> USD{" "}
              {summary.paid_amount.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
        )}
      </Card>

      {/* Upload Modal */}
      <Modal
        title={`Upload ${uploadType === "bill" ? "Bills" : "Invoices"} File`}
        visible={uploadModalVisible}
        onOk={handleUploadOk}
        onCancel={handleUploadCancel}
        confirmLoading={uploadLoading}
        okText="Upload"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="file"
            label="Excel File"
            rules={[{ required: true, message: "Please upload an Excel file" }]}
          >
            <Upload.Dragger {...uploadProps} maxCount={1}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Click or drag Excel file to this area to upload
              </p>
              <p className="ant-upload-hint">
                Only Excel files (.xlsx, .xls) are supported
              </p>
              <p
                className="ant-upload-hint"
                style={{
                  marginTop: "8px",
                  fontWeight: "bold",
                  color: "#1890ff",
                }}
              >
                Uploading as: {uploadType === "bill" ? "Bill" : "Invoice"}
              </p>
            </Upload.Dragger>
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea
              rows={4}
              placeholder={`Add any notes about this ${
                uploadType === "bill" ? "bills" : "invoices"
              } file`}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Excel Export Modal */}
      <Modal
        title="Export Excel"
        visible={isExportModalVisible}
        onCancel={() => setIsExportModalVisible(false)}
        footer={null}
        centered
      >
        <div style={{ textAlign: "center" }}>
          <p>Do you want to download the exported file or send it via email?</p>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 16,
            }}
          >
            <Button className="btn btn-primary w-50" onClick={handleDownload}>
              Download
            </Button>
            <Input
              type="email"
              placeholder="Enter email address"
              className="form-control"
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
              style={{ width: "100%" }}
            />
            <Button
              className="btn btn-secondary w-50"
              onClick={handleEmailSend}
              loading={emailLoading}
            >
              Share via Email
            </Button>
          </div>
        </div>
      </Modal>

      {/* Create Invoice Modal */}
      <CreateInvoiceModal
        visible={createInvoiceModalVisible}
        onCancel={() => setCreateInvoiceModalVisible(false)}
        onSuccess={handleCreateInvoiceSuccess}
        selectedBrand={selectedBrand}
      />
    </div>
  );
};

export default Invoices;
